import type {
  AdvisorDraft,
  AdvisorDraftDay,
  AdvisorDraftItem,
  AdvisorDraftLeg,
} from './types';
import { orderDayItems } from './add-item';
import { advisorRegionKey } from './region-layout';

const nextId = (prefix: string): string =>
  `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

const regionKey = (title: string): string => advisorRegionKey(title);

const hasKind = (day: AdvisorDraftDay, kind: string): boolean =>
  day.items.some((item) => item.kind === kind);

const kindCount = (day: AdvisorDraftDay, kind: string): number =>
  day.items.filter((item) => item.kind === kind).length;

// Night anchor: any hotel already on the day (manual or suggested) blocks
// another suggested hotel — repeated Generate must not stack hotels.
// Other kinds keep the manual-only gate: experiences and restaurants
// legitimately repeat (morning/afternoon, lunch/dinner).
const suggestedBlocked = (
  cur: AdvisorDraftDay | undefined,
  kind: string
): boolean => {
  if (!cur) return false;
  if (kind === 'hotel') return hasKind(cur, 'hotel');
  return cur.items.some(
    (item) => item.origin === 'manual' && item.kind === kind
  );
};

const MAX_FOLDED_RESTAURANTS = 2;
const MAX_FOLDED_EXPERIENCES = 2;

/** Room on an existing day when folding leftover incoming nights. */
const roomForFolded = (day: AdvisorDraftDay, kind: string): boolean => {
  if (suggestedBlocked(day, kind)) return false;
  if (kind === 'hotel') return !hasKind(day, 'hotel');
  if (kind === 'restaurant') {
    return kindCount(day, 'restaurant') < MAX_FOLDED_RESTAURANTS;
  }
  if (kind === 'experience') {
    return kindCount(day, 'experience') < MAX_FOLDED_EXPERIENCES;
  }
  return true;
};

// Fill empty / extra days of one region around manual pins. Never drops
// a manual item. Suggested slots skip when the product is already on the
// leg, the day already has a manual of the same kind, or — for the night
// anchor — the day already has ANY hotel. An empty extra night keeps the
// previous hotel (a stay) and takes food/experiences from Generate.
// Extra incoming nights fold into existing days instead of appending.
export const mergeGeneratedLeg = (
  current: AdvisorDraftLeg,
  incoming: AdvisorDraftLeg,
  onlyDayId: string | null = null
): AdvisorDraftLeg => {
  const used = new Set(
    current.days.flatMap((day) => day.items.map((item) => item.productId))
  );
  if (onlyDayId) {
    const index = current.days.findIndex((day) => day.id === onlyDayId);
    const cur = index >= 0 ? current.days[index] : undefined;
    if (!cur) return current;
    const items = [...cur.items];
    if (!hasKind(cur, 'hotel')) {
      for (let back = index - 1; back >= 0; back -= 1) {
        const previous = current.days[back]?.items.find(
          (item) => item.kind === 'hotel'
        );
        if (!previous) continue;
        items.push({ ...previous, id: nextId('item') });
        break;
      }
    }
    const filled: AdvisorDraftDay = { ...cur, items };
    const extra: typeof cur.items = [];
    const lastFull =
      incoming.days.length >= 2
        ? incoming.days[incoming.days.length - 2]
        : incoming.days[0];
    const sug =
      incoming.days.length === 1
        ? incoming.days[0]
        : index < incoming.days.length - 1
          ? (incoming.days[index] ?? lastFull)
          : lastFull;
    for (const item of sug?.items ?? []) {
      if (item.kind !== 'hotel' && used.has(item.productId)) continue;
      if (suggestedBlocked(filled, item.kind)) continue;
      extra.push(item);
      used.add(item.productId);
      filled.items = [...items, ...extra];
    }
    const nextDay = {
      id: cur.id,
      items: orderDayItems([...items, ...extra]),
    };
    return {
      ...current,
      regionTitle: current.regionTitle,
      days: current.days.map((day) => (day.id === onlyDayId ? nextDay : day)),
    };
  }
  if (current.days.length === 0) {
    const days: AdvisorDraftDay[] = [];
    for (const sug of incoming.days) {
      const items = sug.items.filter((item) => {
        if (used.has(item.productId)) return false;
        used.add(item.productId);
        return true;
      });
      days.push({
        id: sug.id ?? nextId('day'),
        items: orderDayItems(items),
      });
    }
    return { ...current, regionTitle: current.regionTitle, days };
  }
  const days: AdvisorDraftDay[] = [];
  for (let index = 0; index < current.days.length; index += 1) {
    const cur = current.days[index];
    if (!cur) continue;
    const items = [...cur.items];
    if (!hasKind(cur, 'hotel')) {
      for (let back = days.length - 1; back >= 0; back -= 1) {
        const previous = days[back]?.items.find(
          (item) => item.kind === 'hotel'
        );
        if (!previous) continue;
        items.push({ ...previous, id: nextId('item') });
        break;
      }
    }
    const filled: AdvisorDraftDay = { ...cur, items };
    const sug = incoming.days[index];
    const extra = (sug?.items ?? []).filter((item) => {
      if (item.kind !== 'hotel' && used.has(item.productId)) return false;
      if (suggestedBlocked(filled, item.kind)) return false;
      return true;
    });
    for (const item of extra) used.add(item.productId);
    days.push({
      id: cur.id,
      items: orderDayItems([...items, ...extra]),
    });
  }
  for (
    let index = current.days.length;
    index < incoming.days.length;
    index += 1
  ) {
    for (const item of incoming.days[index]?.items ?? []) {
      if (used.has(item.productId)) continue;
      const target = days.find((day) => roomForFolded(day, item.kind));
      if (!target) continue;
      target.items.push(item);
      used.add(item.productId);
    }
  }
  return {
    ...current,
    regionTitle: current.regionTitle,
    days: days.map((day) => ({
      ...day,
      items: orderDayItems(day.items),
    })),
  };
};

const PROGRAMME_KINDS = ['hotel', 'restaurant', 'experience'] as const;

const cloneSuggested = (item: AdvisorDraftItem): AdvisorDraftItem => ({
  ...item,
  id: nextId('item'),
  origin: 'suggested',
});

/**
 * Later nights keep a hotel stay + a meal when sold-mass thins out.
 * Experiences are trip-unique: never clone a productId already on the leg
 * (Tapas on night 1 must not reappear as "fill" on night 4).
 * Restaurants may reuse only when no unused dining product remains.
 * Final pass drops experience duplicates the builder may still have emitted.
 */
export const fillMissingProgramme = (leg: AdvisorDraftLeg): AdvisorDraftLeg => {
  const pool = (kind: (typeof PROGRAMME_KINDS)[number]): AdvisorDraftItem[] =>
    leg.days.flatMap((day) => day.items.filter((item) => item.kind === kind));

  const usedProductIds = new Set(
    leg.days.flatMap((day) => day.items.map((item) => item.productId))
  );

  const days = leg.days.map((day, index) => {
    let items = [...day.items];
    for (const kind of PROGRAMME_KINDS) {
      if (hasKind({ ...day, items }, kind)) continue;
      if (kind === 'hotel') {
        for (let back = index - 1; back >= 0; back -= 1) {
          const previous = leg.days[back]?.items.find(
            (item) => item.kind === 'hotel'
          );
          if (!previous) continue;
          items = [...items, cloneSuggested(previous)];
          break;
        }
        continue;
      }
      const candidates = pool(kind);
      const unused = candidates.find(
        (item) =>
          !items.some((entry) => entry.productId === item.productId) &&
          !usedProductIds.has(item.productId)
      );
      if (unused) {
        items = [...items, cloneSuggested(unused)];
        usedProductIds.add(unused.productId);
        continue;
      }
      // Experiences stay unique for the trip. Empty is better than a repeat tour.
      if (kind === 'experience') continue;
      // Dining: one soft reuse when the catalogue night had no other restaurant.
      const source = candidates.find(
        (item) => !items.some((entry) => entry.productId === item.productId)
      );
      if (!source) continue;
      items = [...items, cloneSuggested(source)];
      usedProductIds.add(source.productId);
    }
    return { ...day, items: orderDayItems(items) };
  });
  const seenExperience = new Set<string>();
  return {
    ...leg,
    days: days.map((day) => ({
      ...day,
      items: orderDayItems(
        day.items.filter((item) => {
          if (item.kind !== 'experience') return true;
          if (seenExperience.has(item.productId)) return false;
          seenExperience.add(item.productId);
          return true;
        })
      ),
    })),
  };
};

export const mergeGeneratedDraft = (
  current: AdvisorDraft,
  incoming: AdvisorDraft,
  onlyLegId: string | null,
  onlyDayId: string | null = null
): AdvisorDraft => {
  // An empty rail takes the preview's legs; the proposal keeps its own title
  // and reference (the host saves it under them).
  if (current.legs.length === 0 && onlyLegId === null) {
    return {
      ...current,
      legs: incoming.legs.map((leg) =>
        fillMissingProgramme({
          ...leg,
          days: leg.days.map((day) => ({
            ...day,
            items: orderDayItems(day.items),
          })),
        })
      ),
    };
  }
  if (onlyLegId) {
    return {
      ...current,
      legs: current.legs.map((leg) => {
        if (leg.id !== onlyLegId) return leg;
        const match =
          incoming.legs.find(
            (entry) =>
              regionKey(entry.regionTitle) === regionKey(leg.regionTitle)
          ) ?? incoming.legs[0];
        return match
          ? fillMissingProgramme(mergeGeneratedLeg(leg, match, onlyDayId))
          : leg;
      }),
    };
  }
  const legs = current.legs.map((leg) => {
    const match = incoming.legs.find(
      (entry) => regionKey(entry.regionTitle) === regionKey(leg.regionTitle)
    );
    return match ? fillMissingProgramme(mergeGeneratedLeg(leg, match)) : leg;
  });
  return { ...current, legs };
};

export const removeSuggestedFromDraft = (
  draft: AdvisorDraft,
  onlyLegId: string | null
): AdvisorDraft => ({
  ...draft,
  legs: draft.legs.flatMap((leg) => {
    if (onlyLegId && leg.id !== onlyLegId) return [leg];
    const days = leg.days.flatMap((day) => {
      const hadSuggestedOnly =
        day.items.length > 0 &&
        day.items.every((item) => item.origin === 'suggested');
      const items = day.items.filter((item) => item.origin !== 'suggested');
      if (items.length === 0 && hadSuggestedOnly) return [];
      return [{ ...day, items }];
    });
    return days.length > 0 || leg.days.length === 0 ? [{ ...leg, days }] : [];
  }),
});

export const draftHasSuggested = (draft: AdvisorDraft): boolean =>
  draft.legs.some((leg) =>
    leg.days.some((day) =>
      day.items.some((item) => item.origin === 'suggested')
    )
  );

export const legHasSuggested = (leg: AdvisorDraftLeg): boolean =>
  leg.days.some((day) => day.items.some((item) => item.origin === 'suggested'));
